import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import GoogleTrustBadge from "@/components/GoogleTrustBadge";

const BENEFITS = [
  { icon: "🎓", title: "Reach Canadian learners",
    body: "Publish your courses on Teacher4Me's growing platform and gain visibility with students, parents, and adult learners." },
  { icon: "🛡️", title: "Trusted co-branding",
    body: "Every completion issues a dual certificate — your organization + Teacher4Me — building trust with employers." },
  { icon: "💳", title: "Reuse our Stripe checkout",
    body: "No merchant setup. Learners pay via our proven live Stripe integration. Track transactions in your dashboard." },
  { icon: "🎬", title: "Selective media, per-partner quota",
    body: "Upload only what you need — 500 MB starter quota. No bulk library imports. Videos, images, PDFs supported." },
  { icon: "🤖", title: "AI tutor available",
    body: "Enable our built-in AI tutor to answer student questions scoped to your course content." },
  { icon: "📋", title: "Compliance-first",
    body: "Automated screening for regulated keywords. Admin review keeps your content credible and safe to publish." },
];

export default function PartnersLanding() {
  const [partners, setPartners] = useState([]);

  useEffect(() => {
    api.get("/api/partners").then(({ data }) => setPartners(data)).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-[#F7F5F0]">
      {/* Hero */}
      <section className="bg-[#064E3B] text-white py-20 px-6" data-testid="partners-hero">
        <div className="max-w-5xl mx-auto">
          <Badge className="bg-white/15 text-white border-white/20 mb-4">
            Teacher4Me Training Partner Portal
          </Badge>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight mb-6">
            Bring your training. <br/>
            <span className="text-[#F59E0B]">Reach Canadian learners.</span>
          </h1>
          <p className="text-lg sm:text-xl text-white/85 max-w-2xl mb-8">
            A multi-tenant learning platform for accredited training organizations.
            Publish courses, manage cohorts, and issue dual certificates — without rebuilding anything.
          </p>
          <div className="flex flex-wrap gap-3">
            <Link to="/partners/apply">
              <Button
                data-testid="partners-cta-apply-btn"
                className="bg-[#F59E0B] hover:bg-[#D97706] text-black font-semibold px-8 py-6 text-base rounded-full"
              >
                Apply to become a partner
              </Button>
            </Link>
            <a href="#how-it-works">
              <Button
                data-testid="partners-cta-learn-btn"
                variant="outline"
                className="bg-transparent border-white/40 text-white hover:bg-white/10 px-8 py-6 text-base rounded-full"
              >
                How it works
              </Button>
            </a>
            <a href="/teacher4me-partner-guide.pdf" target="_blank" rel="noreferrer">
              <Button
                data-testid="partners-cta-guide-btn"
                variant="outline"
                className="bg-transparent border-white/40 text-white hover:bg-white/10 px-8 py-6 text-base rounded-full"
              >
                📄 Download partner guide (PDF)
              </Button>
            </a>
          </div>

          <div className="mt-10 max-w-3xl">
            <GoogleTrustBadge variant="hero" />
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="max-w-6xl mx-auto py-16 px-6">
        <div className="grid md:grid-cols-3 gap-6">
          {BENEFITS.map((b) => (
            <Card key={b.title} className="border-none shadow-sm" data-testid="partners-benefit-card">
              <CardContent className="p-6">
                <div className="text-3xl mb-3">{b.icon}</div>
                <h3 className="font-bold text-lg mb-2">{b.title}</h3>
                <p className="text-sm text-gray-600">{b.body}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      {/* How it works */}
      <section id="how-it-works" className="max-w-5xl mx-auto py-12 px-6">
        <h2 className="text-3xl font-bold mb-8 text-center">How it works</h2>
        <div className="grid md:grid-cols-4 gap-6">
          {[
            ["1", "Apply", "Submit your organization details, accreditations, and a signed e-agreement."],
            ["2", "Get approved", "Our team reviews and approves within 3 business days."],
            ["3", "Build courses", "Create courses, modules, lessons, quizzes — with selective media upload."],
            ["4", "Publish & earn", "Publish, welcome learners, and issue dual certificates. Manual revenue share."],
          ].map(([n, t, b]) => (
            <div key={n} className="text-center" data-testid={`partners-step-${n}`}>
              <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-[#DC2626] text-white grid place-items-center font-bold">
                {n}
              </div>
              <h4 className="font-semibold mb-1">{t}</h4>
              <p className="text-sm text-gray-600">{b}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Existing partners */}
      {partners.length > 0 && (
        <section className="max-w-6xl mx-auto py-12 px-6">
          <h2 className="text-3xl font-bold mb-6">Our training partners</h2>
          <div className="grid md:grid-cols-3 gap-6">
            {partners.map((p) => (
              <Link key={p.id} to={`/partners/${p.slug}`} data-testid="partners-directory-card"
                className="group">
                <Card className="h-full hover:shadow-lg transition-shadow">
                  <CardHeader>
                    <CardTitle className="group-hover:text-[#DC2626] transition-colors">
                      {p.organization_name}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {p.tagline && <p className="text-sm text-gray-600 mb-3">{p.tagline}</p>}
                    {p.accreditations?.length > 0 && (
                      <div className="flex flex-wrap gap-1">
                        {p.accreditations.slice(0, 3).map((a) => (
                          <Badge key={a} variant="secondary" className="text-xs">{a}</Badge>
                        ))}
                      </div>
                    )}
                    <div className="text-xs text-[#DC2626] font-semibold mt-3 group-hover:underline">
                      Visit storefront →
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Final CTA */}
      <section className="bg-[#DC2626] text-white py-16 px-6 mt-8">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold mb-4">
            Ready to bring your training online?
          </h2>
          <p className="text-lg text-white/90 mb-6">
            Applications reviewed within 3 business days.
          </p>
          <Link to="/partners/apply">
            <Button
              data-testid="partners-cta-apply-btn-bottom"
              className="bg-white text-[#DC2626] hover:bg-white/95 font-bold px-8 py-6 rounded-full"
            >
              Apply now
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
